import React, { useEffect, useState, useCallback } from 'react';
import SectorForm from './SectorForm';
import '../../css/sectorList.css';
import apiservice from "../../shared/services/ApiService";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SectorList = () => {
    const [sectors, setSectors] = useState([]);
    const [filteredSectors, setFilteredSectors] = useState([]);
    const [selectedSector, setSelectedSector] = useState(null);
    const [brigades, setBrigades] = useState([]);
    const [circunscripciones, setCircunscripciones] = useState([]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCircunscripcion, setSelectedCircunscripcion] = useState('');
    const [selectedBrigade, setSelectedBrigade] = useState(''); // NUEVO ESTADO
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [displayView, setDisplayView] = useState('grid');
    const [statusFilter, setStatusFilter] = useState('all');
    const [workTypeFilter, setWorkTypeFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [sectorsPerPage] = useState(9);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await Promise.all([
                fetchSectors(),
                fetchBrigades(),
                fetchCircunscripciones()
            ]);
            setIsLoading(false);
        };
        
        fetchData();
    }, []);

    const fetchSectors = async () => {
        try {
            const response = await apiservice.get(`/api/sectores`);
            setSectors(response.data);
        } catch (error) {
            console.error("Error fetching sectors:", error);
            toast.error('Error al cargar sectores');
        }
    };

    const fetchBrigades = async () => {
        try {
            const response = await apiservice.get(`/api/brigadas`);
            setBrigades(response.data);
        } catch (error) {
            console.error("Error fetching brigades:", error);
        }
    };

    const fetchCircunscripciones = async () => {
        try {
            const response = await apiservice.get(`/api/circunscripciones`);
            setCircunscripciones(response.data);
        } catch (error) {
            console.error("Error fetching circunscripciones:", error);
        }
    };

    const filterSectors = useCallback(() => {
        let filtered = sectors.filter(sector => {
            const matchesCircunscripcion = selectedCircunscripcion ? sector.circunscripcion === selectedCircunscripcion : true;
            const matchesBrigade = selectedBrigade ? sector.assignedBrigade?._id === selectedBrigade : true;
            const matchesSearchText = sector.name.toLowerCase().includes(searchText.toLowerCase());
            const matchesStatus = statusFilter === 'all' || sector.status === statusFilter;
            const matchesWorkType = workTypeFilter === 'all' || (sector.workType || 'mixed') === workTypeFilter;

            const sectorDate = new Date(sector.scheduledDate);
            const isWithinDateRange =
                (!startDate || sectorDate >= new Date(startDate)) &&
                (!endDate || sectorDate <= new Date(endDate));

            return matchesCircunscripcion && matchesBrigade && matchesSearchText && isWithinDateRange && matchesStatus && matchesWorkType;
        });

        filtered = [...filtered].sort((a, b) => {
            if (sortBy === 'name') {
                return a.name.localeCompare(b.name);
            } else if (sortBy === 'date') {
                return new Date(a.scheduledDate) - new Date(b.scheduledDate);
            } else if (sortBy === 'status') {
                return a.status.localeCompare(b.status);
            }
            return 0;
        });

        setFilteredSectors(filtered);
        setCurrentPage(1);
    }, [sectors, selectedCircunscripcion, selectedBrigade, searchText, startDate, endDate, statusFilter, workTypeFilter, sortBy]);

    useEffect(() => {
        filterSectors();
    }, [filterSectors]);

    const statistics = {
        total: sectors.length,
        pendiente: sectors.filter(s => s.status === 'Pendiente').length,
        proceso: sectors.filter(s => s.status === 'Proceso').length,
        listo: sectors.filter(s => s.status === 'Listo').length,
    };

    const indexOfLastSector = currentPage * sectorsPerPage;
    const indexOfFirstSector = indexOfLastSector - sectorsPerPage;
    const currentSectors = filteredSectors.slice(indexOfFirstSector, indexOfLastSector);
    const totalPages = Math.ceil(filteredSectors.length / sectorsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleDelete = async (id) => {
        setConfirmDelete(null);
        try {
            await apiservice.delete(`/api/sectores/${id}`);
            toast.success('Sector eliminado correctamente');
            fetchSectors();
        } catch (error) {
            console.error("Error deleting sector:", error);
            toast.error('Error al eliminar sector');
        }
    };

    const handleEdit = (sector) => {
        setSelectedSector(sector);
        setModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedSector(null);
        setModalOpen(true);
    };

    const handleSubmit = async (sectorData) => {
        try {
            if (selectedSector) {
                await apiservice.put(`/api/sectores/${selectedSector._id}`, sectorData);
                toast.success('Sector actualizado correctamente');
            } else {
                await apiservice.post(`/api/sectores`, sectorData);
                toast.success('Sector creado correctamente');
            }
            setModalOpen(false);
            setSelectedSector(null);
            fetchSectors();
        } catch (error) {
            console.error("Error saving sector:", error);
            toast.error(selectedSector ? 'Error al actualizar sector' : 'Error al crear sector');
        }
    };

    const getCircunscripcionName = (id) => {
        const circunscripcion = circunscripciones.find(c => c._id === id);
        return circunscripcion ? circunscripcion.name : 'No asignada';
    };

    const getBrigadeName = (id) => {
        const brigade = brigades.find(b => b._id === id);
        return brigade ? brigade.name : 'No asignada';
    };

    const getStatusClass = (status) => {
        switch(status) {
            case 'Pendiente': return 'status-pending-sector';
            case 'Proceso': return 'status-in-progress-sector';
            case 'Listo': return 'status-completed-sector';
            default: return '';
        }
    };

    const getWorkTypeLabel = (type) => {
        switch(type) {
            case 'trimmer': return 'Corte de Cesped';
            case 'tree': return 'Poda de Arboles';
            case 'ornamental': return 'Ornato';
            case 'mixed': return 'Mixto';
            default: return 'Mixto';
        }
    };

    const clearFilters = () => {
        setSelectedCircunscripcion('');
        setSelectedBrigade('');
        setSearchText('');
        setStartDate('');
        setEndDate('');
        setStatusFilter('all');
        setWorkTypeFilter('all');
    };

    return (
        <div className="sectors-management-container">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="sectors-header-modern">
                <div className="header-content-sectors">
                    <div className="header-title-section-sectors">
                        <div className="title-icon-sectors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="page-title-sectors">Gestión de Sectores</h1>
                            <p className="page-subtitle-sectors">Administra los sectores y sus coordenadas</p>
                        </div>
                    </div>
                    <div className="header-actions-sectors">
                        <button className="btn-primary-sectors" onClick={handleAdd}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"/>
                                <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            Nuevo Sector
                        </button>
                    </div>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="stats-section-sectors">
                <div className="stat-card-sectors total">
                    <div className="stat-icon-sectors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                        </svg>
                    </div>
                    <div className="stat-content-sectors">
                        <h3>{statistics.total}</h3>
                        <p>Total Sectores</p>
                    </div>
                </div>

                <div className="stat-card-sectors pending">
                    <div className="stat-icon-sectors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                        </svg>
                    </div>
                    <div className="stat-content-sectors">
                        <h3>{statistics.pendiente}</h3>
                        <p>Pendientes</p>
                    </div>
                </div>

                <div className="stat-card-sectors progress">
                    <div className="stat-icon-sectors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                    </div>
                    <div className="stat-content-sectors">
                        <h3>{statistics.proceso}</h3>
                        <p>En Proceso</p>
                    </div>
                </div>

                <div className="stat-card-sectors completed">
                    <div className="stat-icon-sectors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                    </div>
                    <div className="stat-content-sectors">
                        <h3>{statistics.listo}</h3>
                        <p>Completados</p>
                    </div>
                </div>
            </div>

            {/* Tabs de Tipo de Trabajo */}
            <div className="work-type-tabs-sectors">
                {[
                    { value: 'all', label: 'Todos' },
                    { value: 'trimmer', label: 'Corte de Cesped' },
                    { value: 'tree', label: 'Poda de Arboles' },
                    { value: 'ornamental', label: 'Ornato' },
                    { value: 'mixed', label: 'Mixto' }
                ].map(tab => (
                    <button
                        key={tab.value}
                        className={`work-type-tab-sectors ${workTypeFilter === tab.value ? 'active' : ''}`}
                        onClick={() => setWorkTypeFilter(tab.value)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Filtros */}
            <div className="filter-section-sectors">
                <div className="search-box-sectors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input 
                        type="text"
                        placeholder="Buscar sectores..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                        <button 
                            className="clear-search-btn-sectors"
                            onClick={() => setSearchText('')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    )}
                </div>

                <div className="filters-row-sectors">
                    <select 
                        className="filter-select-sectors"
                        value={selectedCircunscripcion} 
                        onChange={(e) => setSelectedCircunscripcion(e.target.value)}
                    >
                        <option value="">Todas las circunscripciones</option>
                        {circunscripciones.map((circunscripcion) => (
                            <option key={circunscripcion._id} value={circunscripcion._id}>
                                {circunscripcion.name}
                            </option>
                        ))}
                    </select>

                    {/* NUEVO FILTRO POR BRIGADAS */}
                    <select 
                        className="filter-select-sectors"
                        value={selectedBrigade} 
                        onChange={(e) => setSelectedBrigade(e.target.value)}
                    >
                        <option value="">Todas las brigadas</option>
                        {brigades.map((brigade) => (
                            <option key={brigade._id} value={brigade._id}>
                                {brigade.name}
                            </option>
                        ))}
                    </select>

                    <select 
                        className="filter-select-sectors"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Todos los estados</option>
                        <option value="Pendiente">Pendiente</option>
                        <option value="Proceso">En Proceso</option>
                        <option value="Listo">Completado</option>
                    </select>

                    <input
                        type="date"
                        className="filter-date-sectors"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        placeholder="Desde"
                    />

                    <input
                        type="date"
                        className="filter-date-sectors"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        placeholder="Hasta"
                    />

                    <select 
                        className="filter-select-sectors"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="name">Ordenar por nombre</option>
                        <option value="date">Ordenar por fecha</option>
                        <option value="status">Ordenar por estado</option>
                    </select>

                    <button className="btn-clear-filters-sectors" onClick={clearFilters}>
                        Limpiar Filtros
                    </button>
                </div>
            </div>

            {/* Controles de vista */}
            <div className="view-controls-sectors">
                <div className="view-toggles-sectors">
                    <button 
                        className={`view-toggle-sectors ${displayView === 'grid' ? 'active' : ''}`}
                        onClick={() => setDisplayView('grid')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7"/>
                            <rect x="14" y="3" width="7" height="7"/>
                            <rect x="14" y="14" width="7" height="7"/>
                            <rect x="3" y="14" width="7" height="7"/>
                        </svg>
                    </button>
                    <button 
                        className={`view-toggle-sectors ${displayView === 'list' ? 'active' : ''}`}
                        onClick={() => setDisplayView('list')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"/>
                            <line x1="8" y1="12" x2="21" y2="12"/>
                            <line x1="8" y1="18" x2="21" y2="18"/>
                            <line x1="3" y1="6" x2="3.01" y2="6"/>
                            <line x1="3" y1="12" x2="3.01" y2="12"/>
                            <line x1="3" y1="18" x2="3.01" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div className="results-info-sectors">
                    Mostrando {indexOfFirstSector + 1}-{Math.min(indexOfLastSector, filteredSectors.length)} de {filteredSectors.length} sectores
                </div>
            </div>

            {/* Contenido */}
            {isLoading ? (
                <div className="loading-state-sectors">
                    <div className="spinner-sectors"></div>
                    <p>Cargando sectores...</p>
                </div>
            ) : (
                <>
                    {currentSectors.length > 0 ? (
                        displayView === 'grid' ? (
                            <div className="sectors-grid-modern">
                                {currentSectors.map((sector) => (
                                    <div className="sector-card-modern" key={sector._id}>
                                        <div className="sector-card-header-modern">
                                            <div className="sector-avatar-modern">
                                                {sector.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="sector-title-modern">
                                                <h3>{sector.name}</h3>
                                                <div className="sector-badges-row">
                                                    <span className={`status-badge-sectors ${getStatusClass(sector.status)}`}>
                                                        {sector.status}
                                                    </span>
                                                    <span className={`work-type-badge-sectors wt-${sector.workType || 'mixed'}`}>
                                                        {getWorkTypeLabel(sector.workType)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="sector-card-body-modern">
                                            <div className="sector-detail-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                                </svg>
                                                <span>{getCircunscripcionName(sector.circunscripcion)}</span>
                                            </div>
                                            <div className="sector-detail-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="9" cy="7" r="4"/>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                                <span>{getBrigadeName(sector.assignedBrigade?._id)}</span>
                                            </div>
                                            {sector.scheduledDate && (
                                                <div className="sector-detail-item">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                                    </svg>
                                                    <span>{new Date(sector.scheduledDate).toLocaleDateString()}</span>
                                                </div>
                                            )}
                                            <div className="sector-detail-item">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                                    <circle cx="12" cy="10" r="3"/>
                                                </svg>
                                                <span>{sector.coordinates ? sector.coordinates.length : 0} coordenadas</span>
                                            </div>
                                        </div>
                                        <div className="sector-card-footer-modern">
                                            <button className="action-btn-sectors edit" onClick={() => handleEdit(sector)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            <button className="action-btn-sectors delete" onClick={() => setConfirmDelete(sector._id)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"/>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sectors-table-container">
                                <table className="sectors-table-modern">
                                    <thead>
                                        <tr>
                                            <th>Sector</th>
                                            <th>Circunscripción</th>
                                            <th>Estado</th>
                                            <th>Brigada</th>
                                            <th>Fecha</th>
                                            <th>Coordenadas</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSectors.map((sector) => (
                                            <tr key={sector._id}>
                                                <td className="sector-name-cell">{sector.name}</td>
                                                <td>{getCircunscripcionName(sector.circunscripcion)}</td>
                                                <td>
                                                    <span className={`status-badge-sectors ${getStatusClass(sector.status)}`}>
                                                        {sector.status}
                                                    </span>
                                                </td>
                                                <td>{getBrigadeName(sector.assignedBrigade)}</td>
                                                <td>
                                                    {sector.scheduledDate && new Date(sector.scheduledDate).toLocaleDateString()}
                                                </td>
                                                <td>{sector.coordinates ? sector.coordinates.length : 0}</td>
                                                <td className="action-buttons-table">
                                                    <button className="action-btn-sectors edit" onClick={() => handleEdit(sector)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                        </svg>
                                                    </button>
                                                    <button className="action-btn-sectors delete" onClick={() => setConfirmDelete(sector._id)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : (
                        <div className="empty-state-sectors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <h3>No hay sectores</h3>
                            <p>
                                {searchText || selectedCircunscripcion || selectedBrigade || statusFilter !== 'all' || startDate || endDate
                                    ? 'No se encontraron sectores con los filtros aplicados'
                                    : 'No hay sectores registrados. Crea uno nuevo para comenzar.'}
                            </p>
                            {(searchText || selectedCircunscripcion || selectedBrigade || statusFilter !== 'all' || startDate || endDate) && (
                                <button className="btn-clear-filters-sectors" onClick={clearFilters}>
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    )}

                    {/* Paginación */}
                    {totalPages > 1 && (
                        <div className="pagination-sectors">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="page-btn-sectors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6"/>
                                </svg>
                            </button>

                            <div className="page-numbers-sectors">
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(num =>
                                        num === 1 ||
                                        num === totalPages ||
                                        (num >= currentPage - 1 && num <= currentPage + 1)
                                    )
                                    .map((number, index, array) => {
                                        if (index > 0 && array[index - 1] !== number - 1) {
                                            return (
                                                <React.Fragment key={`ellipsis-${number}`}>
                                                    <span className="ellipsis-sectors">...</span>
                                                    <button
                                                        onClick={() => paginate(number)}
                                                        className={`page-btn-sectors ${currentPage === number ? 'active' : ''}`}
                                                    >
                                                        {number}
                                                    </button>
                                                </React.Fragment>
                                            );
                                        }
                                        return (
                                            <button
                                                key={number}
                                                onClick={() => paginate(number)}
                                                className={`page-btn-sectors ${currentPage === number ? 'active' : ''}`}
                                            >
                                                {number}
                                            </button>
                                        );
                                    })
                                }
                            </div>

                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="page-btn-sectors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Modal de formulario */}
            {isModalOpen && (
                <SectorForm
                    isOpen={isModalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedSector(null);
                    }}
                    onSubmit={handleSubmit}
                    sector={selectedSector}
                    brigades={brigades}
                    circunscripciones={circunscripciones}
                    isEditing={!!selectedSector}
                />
            )}

            {/* Modal de confirmación */}
            {confirmDelete && (
                <div className="modal-overlay-sector" onClick={() => setConfirmDelete(null)}>
                    <div className="confirm-dialog-sectors" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-icon-sectors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <h3>Confirmar Eliminación</h3>
                        <p>¿Estás seguro de que deseas eliminar este sector? Esta acción no se puede deshacer.</p>
                        <div className="confirm-actions-sectors">
                            <button className="btn-cancel-confirm" onClick={() => setConfirmDelete(null)}>
                                Cancelar
                            </button>
                            <button className="btn-confirm-delete" onClick={() => handleDelete(confirmDelete)}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SectorList;